import { Box, Grid, InputLabel, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
    useGetAllClientsQuery,
    useGetEarningsQuery,
    useGetPlatformSettingsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import LoadingComponent from '../../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../../shared/AuthRoles';
import FormSelect from '../../../../shared/FormSelect';
import HostPaymentsReportList from './HostPaymentsReportList';

export const HostPaymentsReportPage = () => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    useEffect(() => {
        if (loggedInUser.role !== 'admin') {
            navigate(`/permissionDenied`);
        }
    }, []);

    const now = new Date();
    const currentYear = now.getFullYear();

    const { control, watch } = useForm<{
        year: number;
        owner: number;
        projected: boolean;
    }>({
        defaultValues: { year: currentYear, owner: 0, projected: false },
    });

    const { data: earnings } = useGetEarningsQuery({
        year: watch('year'),
        projected: watch('projected'),
        ...(watch('owner') ? { owner: watch('owner') } : {}),
    });
    const { data: owners } = useGetAllClientsQuery('/owners');
    const { data: platformSettings } =
        useGetPlatformSettingsQuery('/platform-settings');

    const [years, setYears] = useState<{ value: number; label: string }[]>([]);

    const projected = useWatch({ control, name: 'projected' });

    useEffect(() => {
        if (projected) {
            setYears(
                [currentYear, currentYear + 1].map((year) => ({
                    value: year,
                    label: `${year}`,
                })),
            );
        } else {
            const pastYears = [];
            for (let year = currentYear; year >= 2023; year--) {
                pastYears.push({
                    value: year,
                    label: `${year}`,
                });
            }
            setYears(pastYears);
        }
    }, [projected]);

    return !earnings || !owners || !platformSettings ? (
        <LoadingComponent />
    ) : (
        <Box sx={{ Top: isMobile ? '30px' : '0px' }}>
            <Grid
                container
                justifyContent={'space-between'}
                spacing={1}
                alignItems={'center'}
                sx={{ paddingBottom: 2 }}
            >
                <Grid
                    container
                    item
                    xs={12}
                    lg={8}
                    spacing={1}
                    alignItems={'flex-end'}
                >
                    <Grid item xs={12} lg={2.5}>
                        <InputLabel>Payment Year</InputLabel>
                        <FormSelect
                            name='year'
                            control={control}
                            options={years}
                        />
                    </Grid>
                    <Grid item xs={12} lg={3.5}>
                        <InputLabel>Owner</InputLabel>
                        <FormSelect
                            name='owner'
                            control={control}
                            options={[
                                { value: 0, label: 'All' },
                                ...owners.map(
                                    (owner: { id: number; name: string }) => ({
                                        value: owner.id,
                                        label: owner.name,
                                    }),
                                ),
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} lg={3.5}>
                        <InputLabel>Mode</InputLabel>
                        <FormSelect
                            name='projected'
                            control={control}
                            options={[
                                { value: true, label: 'Future Earnings' },
                                { value: false, label: 'Actual' },
                            ]}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <HostPaymentsReportList
                earnings={earnings}
                serviceFeePercentage={platformSettings.hostServiceFeePercentage}
            />
        </Box>
    );
};
